import { Extension, mergeAttributes, Node } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import { columnResizing, tableEditing } from '@tiptap/pm/tables';
import StarterKit from '@tiptap/starter-kit';

function readPositiveInteger(value: string | null, fallback: number) {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : fallback;
}

function parseColwidth(value: string | null) {
  if (!value || !/^\d+(,\d+)*$/.test(value)) {
    return null;
  }

  return value.split(',').map((item) => Number(item));
}

function normalizeCssColor(value: unknown) {
  if (typeof value !== 'string') {
    return '';
  }

  const color = value.trim();
  if (!color || /[;{}]|url\(|expression\(/i.test(color)) {
    return '';
  }

  return color;
}

function normalizeTextAlign(value: unknown) {
  return value === 'center' || value === 'right' || value === 'left' ? value : '';
}

function tableCellAttributes() {
  return {
    colspan: {
      default: 1,
      parseHTML: (element: HTMLElement) => readPositiveInteger(element.getAttribute('colspan'), 1),
      renderHTML: (attributes: { colspan?: number }) =>
        attributes.colspan && attributes.colspan !== 1 ? { colspan: attributes.colspan } : {},
    },
    rowspan: {
      default: 1,
      parseHTML: (element: HTMLElement) => readPositiveInteger(element.getAttribute('rowspan'), 1),
      renderHTML: (attributes: { rowspan?: number }) =>
        attributes.rowspan && attributes.rowspan !== 1 ? { rowspan: attributes.rowspan } : {},
    },
    colwidth: {
      default: null,
      parseHTML: (element: HTMLElement) => {
        const widths = parseColwidth(element.getAttribute('data-colwidth'));
        const colspan = readPositiveInteger(element.getAttribute('colspan'), 1);
        return widths && widths.length === colspan ? widths : null;
      },
      renderHTML: (attributes: { colwidth?: number[] | null }) =>
        Array.isArray(attributes.colwidth) ? { 'data-colwidth': attributes.colwidth.join(',') } : {},
    },
    backgroundColor: {
      default: '',
      parseHTML: (element: HTMLElement) =>
        normalizeCssColor(element.style.backgroundColor || element.getAttribute('bgcolor') || ''),
      renderHTML: (attributes: { backgroundColor?: string }) => {
        const color = normalizeCssColor(attributes.backgroundColor);
        return color ? { style: `background-color: ${color}` } : {};
      },
    },
    textAlign: {
      default: '',
      parseHTML: (element: HTMLElement) =>
        normalizeTextAlign(element.style.textAlign || element.getAttribute('align') || ''),
      renderHTML: (attributes: { textAlign?: string }) => {
        const textAlign = normalizeTextAlign(attributes.textAlign);
        return textAlign ? { style: `text-align: ${textAlign}` } : {};
      },
    },
  };
}

function getYouTubeId(url: string) {
  const match = url.match(
    /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/
  );
  return match?.[1];
}

export function getVideoEmbedUrl(url: string) {
  const youtubeId = getYouTubeId(url);
  if (youtubeId) {
    return `https://www.youtube.com/embed/${youtubeId}`;
  }

  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?([0-9]+)/);
  if (vimeoMatch?.[1]) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  }

  return url;
}

const ContentImage = Node.create({
  name: 'contentImage',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      src: { default: null },
      alt: { default: '' },
      title: { default: '' },
      storagePath: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'img[src]' }];
  },

  renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, unknown> }) {
    return ['img', mergeAttributes(HTMLAttributes, { class: 'content-bank-image' })];
  },
});

const ContentVideo = Node.create({
  name: 'contentVideo',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      url: { default: '' },
      embedUrl: { default: '' },
      title: { default: '' },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-content-video]' }];
  },

  renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, unknown> }) {
    const url = typeof HTMLAttributes.url === 'string' ? HTMLAttributes.url : '';
    const embedUrl =
      typeof HTMLAttributes.embedUrl === 'string' && HTMLAttributes.embedUrl
        ? HTMLAttributes.embedUrl
        : getVideoEmbedUrl(url);

    return [
      'div',
      mergeAttributes({ class: 'content-bank-video', 'data-content-video': 'true' }),
      [
        'iframe',
        {
          src: embedUrl,
          title: typeof HTMLAttributes.title === 'string' ? HTMLAttributes.title : 'Video',
          allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture',
          allowfullscreen: 'true',
        },
      ],
    ];
  },
});

const ContentTable = Node.create({
  name: 'table',
  group: 'block',
  content: 'tableRow+',
  isolating: true,

  parseHTML() {
    return [{ tag: 'table' }];
  },

  renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, unknown> }) {
    return [
      'table',
      mergeAttributes(HTMLAttributes, { class: 'content-bank-table' }),
      ['tbody', 0],
    ];
  },
} as any);

const ContentTableRow = Node.create({
  name: 'tableRow',
  content: '(tableCell | tableHeader)*',

  parseHTML() {
    return [{ tag: 'tr' }];
  },

  renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, unknown> }) {
    return ['tr', mergeAttributes(HTMLAttributes), 0];
  },
} as any);

const ContentTableCell = Node.create({
  name: 'tableCell',
  content: 'block+',
  isolating: true,

  addAttributes: tableCellAttributes,

  parseHTML() {
    return [{ tag: 'td' }];
  },

  renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, unknown> }) {
    return ['td', mergeAttributes(HTMLAttributes), 0];
  },
} as any);

const ContentTableHeader = Node.create({
  name: 'tableHeader',
  content: 'block+',
  isolating: true,

  addAttributes: tableCellAttributes,

  parseHTML() {
    return [{ tag: 'th' }];
  },

  renderHTML({ HTMLAttributes }: { HTMLAttributes: Record<string, unknown> }) {
    return ['th', mergeAttributes(HTMLAttributes), 0];
  },
} as any);

const ContentTableRoles = Extension.create({
  name: 'contentTableRoles',

  extendNodeSchema(extension) {
    const tableRoles: Record<string, string> = {
      table: 'table',
      tableRow: 'row',
      tableCell: 'cell',
      tableHeader: 'header_cell',
    };
    const tableRole = tableRoles[extension.name];

    return tableRole ? { tableRole } : {};
  },
});

const ContentTableEditing = Extension.create({
  name: 'contentTableEditing',

  addProseMirrorPlugins() {
    return [columnResizing({ cellMinWidth: 80 }), tableEditing({ allowTableNodeSelection: true })];
  },
});

export function createContentBankExtensions(options: { openLinksOnClick?: boolean } = {}) {
  return [
    StarterKit,
    Underline,
    TextAlign.configure({
      types: ['heading', 'paragraph'],
    }),
    Link.configure({
      openOnClick: Boolean(options.openLinksOnClick),
      autolink: true,
      linkOnPaste: true,
    }),
    ContentImage,
    ContentVideo,
    ContentTable,
    ContentTableRow,
    ContentTableCell,
    ContentTableHeader,
    ContentTableRoles,
    ContentTableEditing,
  ];
}
